import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { authApi } from '../api/authApi';
import { useAuth } from './AuthContext';

export const AUTO_REFRESH_MS = 6 * 60 * 1000;
export const VISIBLE_REFRESH_AFTER_MS = 20 * 1000;

const STORAGE_KEY = 'lt-live-streams';
const LEGACY_KEY = 'lt-youtube-streams';
const ACTIVE_KEY = 'lt-live-active';
const LEGACY_ACTIVE_KEY = 'lt-youtube-active';
const MUTED_KEY = 'lt-live-muted';
const PIP_KEY = 'lt-live-pip';

export const DEFAULT_STREAMS = [
  {
    id: 'QB5BNdBFujE',
    label: 'Live desk',
    type: 'youtube',
    source: 'QB5BNdBFujE',
    url: 'https://www.youtube.com/watch?v=QB5BNdBFujE',
  },
];

function hostOf(url) {
  return url.hostname.replace(/^www\./, '');
}

export function parseStreamInput(raw) {
  const input = (raw || '').trim();
  if (!input) return null;

  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
    return {
      type: 'youtube',
      source: input,
      url: `https://www.youtube.com/watch?v=${input}`,
    };
  }

  let url;
  try {
    url = new URL(input.includes('://') ? input : `https://${input}`);
  } catch {
    return null;
  }

  const host = hostOf(url);
  const path = url.pathname.replace(/\/+$/, '');

  if (host === 'youtu.be') {
    const id = path.split('/').filter(Boolean)[0];
    if (id) {
      return { type: 'youtube', source: id, url: `https://www.youtube.com/watch?v=${id}` };
    }
  }

  if (host.includes('youtube.com')) {
    const videoId = url.searchParams.get('v');
    if (videoId) {
      return { type: 'youtube', source: videoId, url: `https://www.youtube.com/watch?v=${videoId}` };
    }
    const liveMatch = path.match(/^\/live\/([^/]+)/);
    if (liveMatch) {
      return { type: 'youtube', source: liveMatch[1], url: `https://www.youtube.com/watch?v=${liveMatch[1]}` };
    }
    const embedMatch = path.match(/^\/embed\/([^/]+)/);
    if (embedMatch?.[1] && embedMatch[1] !== 'live_stream') {
      return { type: 'youtube', source: embedMatch[1], url: `https://www.youtube.com/watch?v=${embedMatch[1]}` };
    }
    const channel = url.searchParams.get('channel') || path.match(/^\/channel\/([^/]+)/)?.[1];
    if (channel) {
      return { type: 'youtube-channel', source: channel, url: `https://www.youtube.com/channel/${channel}/live` };
    }
  }

  if (host.includes('twitch.tv')) {
    const channel = path.split('/').filter(Boolean)[0];
    if (channel && channel !== 'videos') {
      return { type: 'twitch', source: channel, url: `https://www.twitch.tv/${channel}` };
    }
  }

  if (host.includes('vimeo.com')) {
    const id = path.split('/').filter(Boolean).pop();
    if (id) {
      return { type: 'vimeo', source: id, url: `https://vimeo.com/${id}` };
    }
  }

  const ext = path.split('.').pop()?.toLowerCase();
  if (['mp4', 'webm', 'ogg', 'm3u8'].includes(ext) || url.pathname.includes('.m3u8')) {
    return { type: 'file', source: url.href, url: url.href };
  }

  return { type: 'iframe', source: url.href, url: url.href };
}

export function embedSrc(stream, muted = true) {
  const origin = encodeURIComponent(window.location.origin);
  const parent = encodeURIComponent(window.location.hostname);
  const muteFlag = muted ? '1' : '0';
  const mutedBool = muted ? 'true' : 'false';

  if (stream.type === 'youtube') {
    return `https://www.youtube.com/embed/${encodeURIComponent(stream.source)}?autoplay=1&mute=${muteFlag}&rel=0&playsinline=1&origin=${origin}`;
  }
  if (stream.type === 'youtube-channel') {
    return `https://www.youtube.com/embed/live_stream?channel=${encodeURIComponent(stream.source)}&autoplay=1&mute=${muteFlag}&origin=${origin}`;
  }
  if (stream.type === 'twitch') {
    return `https://player.twitch.tv/?channel=${encodeURIComponent(stream.source)}&parent=${parent}&muted=${mutedBool}`;
  }
  if (stream.type === 'vimeo') {
    return `https://player.vimeo.com/video/${encodeURIComponent(stream.source)}?autoplay=1&muted=${muteFlag}`;
  }
  return stream.source;
}

function loadMutedPreference() {
  try {
    const saved = localStorage.getItem(MUTED_KEY);
    if (saved === 'false') return false;
    if (saved === 'true') return true;
  } catch {
    // ignore
  }
  return true;
}

function loadPipPreference() {
  try {
    const saved = localStorage.getItem(PIP_KEY);
    if (saved === 'false') return false;
    if (saved === 'true') return true;
  } catch {
    // ignore
  }
  return true;
}

function normalizeStreams(saved) {
  if (!Array.isArray(saved) || saved.length === 0) return null;
  return saved.map((item) => {
    if (item.type === 'video') {
      return {
        ...item,
        type: 'youtube',
        url: item.url || `https://www.youtube.com/watch?v=${item.source}`,
      };
    }
    if (item.type === 'channel') {
      return {
        ...item,
        type: 'youtube-channel',
        url: item.url || `https://www.youtube.com/channel/${item.source}/live`,
      };
    }
    return item;
  });
}

function persistLocal(streams, activeId) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(streams));
    if (activeId) localStorage.setItem(ACTIVE_KEY, activeId);
  } catch {
    // ignore quota / private mode
  }
}

function loadStreams() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_KEY) || 'null');
    return normalizeStreams(saved) || DEFAULT_STREAMS;
  } catch {
    return DEFAULT_STREAMS;
  }
}

export function defaultStreamLabel(parsed) {
  if (parsed.type === 'youtube' || parsed.type === 'youtube-channel') return 'YouTube';
  if (parsed.type === 'twitch') return `Twitch / ${parsed.source}`;
  if (parsed.type === 'vimeo') return 'Vimeo';
  if (parsed.type === 'file') return 'Direct stream';
  try {
    return new URL(parsed.url).hostname.replace(/^www\./, '');
  } catch {
    return 'Live stream';
  }
}

const LiveStreamContext = createContext(null);

export function LiveStreamProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [streams, setStreams] = useState(() => loadStreams());
  const [activeId, setActiveId] = useState(
    () => localStorage.getItem(ACTIVE_KEY) || localStorage.getItem(LEGACY_ACTIVE_KEY) || DEFAULT_STREAMS[0].id
  );
  const [hydrated, setHydrated] = useState(false);
  const [playerNonce, setPlayerNonce] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [muted, setMuted] = useState(() => loadMutedPreference());
  const [pipEnabled, setPipEnabledState] = useState(() => loadPipPreference());
  const [pipExpanded, setPipExpanded] = useState(true);
  const lastRefreshRef = useRef(Date.now());
  const stallTimerRef = useRef(null);
  const videoRef = useRef(null);

  const refreshPlayer = useCallback(() => {
    setPlayerNonce((n) => n + 1);
    lastRefreshRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setHydrated(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await authApi.getPreferences();
        const remote = response.data || {};
        const remoteStreams = normalizeStreams(remote.liveStreams);
        if (!cancelled && remoteStreams) {
          setStreams(remoteStreams);
          persistLocal(remoteStreams, remote.liveStreamActiveId || remoteStreams[0]?.id);
          if (remote.liveStreamActiveId) {
            setActiveId(remote.liveStreamActiveId);
          }
        }
        if (!cancelled && typeof remote.liveStreamMuted === 'boolean') {
          setMuted(remote.liveStreamMuted);
          try {
            localStorage.setItem(MUTED_KEY, String(remote.liveStreamMuted));
          } catch {
            // ignore
          }
        }
        if (!cancelled && typeof remote.liveStreamPip === 'boolean') {
          setPipEnabledState(remote.liveStreamPip);
          try {
            localStorage.setItem(PIP_KEY, String(remote.liveStreamPip));
          } catch {
            // ignore
          }
        }
      } catch {
        // stay on local copy
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    persistLocal(streams, activeId);
    try {
      localStorage.setItem(MUTED_KEY, String(muted));
      localStorage.setItem(PIP_KEY, String(pipEnabled));
    } catch {
      // ignore
    }
    if (!hydrated) return undefined;

    const timer = setTimeout(() => {
      authApi
        .savePreferences({
          liveStreams: streams,
          liveStreamActiveId: activeId,
          liveStreamMuted: muted,
          liveStreamPip: pipEnabled,
        })
        .catch(() => {});
    }, 400);

    return () => clearTimeout(timer);
  }, [streams, activeId, muted, pipEnabled, hydrated, isAuthenticated]);

  const active = useMemo(
    () => streams.find((s) => s.id === activeId) || streams[0] || null,
    [streams, activeId]
  );

  const addStream = useCallback((urlInput, labelInput = '') => {
    const parsed = parseStreamInput(urlInput);
    if (!parsed) {
      return { ok: false, error: 'Paste a video, live, or embed URL (YouTube, Twitch, Vimeo, or any https stream).' };
    }

    let duplicate = false;
    setStreams((prev) => {
      if (prev.some((s) => s.source === parsed.source && s.type === parsed.type)) {
        duplicate = true;
        return prev;
      }
      const next = {
        id: `${parsed.type}-${parsed.source}`.slice(0, 80),
        label: String(labelInput || '').trim() || defaultStreamLabel(parsed),
        type: parsed.type,
        source: parsed.source,
        url: parsed.url,
      };
      setActiveId(next.id);
      return [...prev, next];
    });

    if (duplicate) {
      return { ok: false, error: 'That stream is already on the desk.' };
    }
    return { ok: true };
  }, []);

  const removeStream = useCallback((id) => {
    setStreams((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (activeId === id && next[0]) {
        setActiveId(next[0].id);
      }
      return next;
    });
  }, [activeId]);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      if (videoRef.current) {
        videoRef.current.muted = next;
        if (!next) {
          videoRef.current.play?.().catch(() => {});
        }
      }
      return next;
    });
  }, []);

  const setPipEnabled = useCallback((value) => {
    setPipEnabledState(Boolean(value));
  }, []);

  const togglePipEnabled = useCallback(() => {
    setPipEnabledState((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!autoRefresh || !active) return undefined;
    const interval = setInterval(refreshPlayer, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, active?.id, refreshPlayer]);

  useEffect(() => {
    const onVisibility = () => {
      if (!autoRefresh) return;
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshRef.current < VISIBLE_REFRESH_AFTER_MS) return;
      refreshPlayer();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, [autoRefresh, refreshPlayer]);

  useEffect(() => {
    return () => {
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    };
  }, []);

  const scheduleStallRefresh = useCallback(() => {
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    stallTimerRef.current = setTimeout(refreshPlayer, 8000);
  }, [refreshPlayer]);

  const clearStallRefresh = useCallback(() => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  }, []);

  const value = useMemo(
    () => ({
      streams,
      active,
      activeId,
      setActiveId,
      muted,
      toggleMuted,
      autoRefresh,
      setAutoRefresh,
      pipEnabled,
      setPipEnabled,
      togglePipEnabled,
      pipExpanded,
      setPipExpanded,
      playerNonce,
      refreshPlayer,
      addStream,
      removeStream,
      videoRef,
      scheduleStallRefresh,
      clearStallRefresh,
    }),
    [
      streams,
      active,
      activeId,
      muted,
      toggleMuted,
      autoRefresh,
      pipEnabled,
      setPipEnabled,
      togglePipEnabled,
      pipExpanded,
      playerNonce,
      refreshPlayer,
      addStream,
      removeStream,
      scheduleStallRefresh,
      clearStallRefresh,
    ]
  );

  return <LiveStreamContext.Provider value={value}>{children}</LiveStreamContext.Provider>;
}

export function useLiveStream() {
  const ctx = useContext(LiveStreamContext);
  if (!ctx) {
    throw new Error('useLiveStream must be used within LiveStreamProvider');
  }
  return ctx;
}
